import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Drawer, Dialog, FlexColumn } from '@elementary/common-ui/components';
import { Tab, Tabs } from '@elementary/common-ui/components';
import { TabContext, TabPanel as TabPanelBase } from '@mui/lab';
import { Box } from '@mui/material';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { GROUP_ID, NEW_VALUE } from '@elementary/common-ui/constants/urlParams';
import styled from 'styled-components';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import { compareArraysValues } from '@elementary/common-ui/utils/common';
import { FormProvider, useForm } from 'react-hook-form';
import { GroupFormData } from '../types';
import { UpdateGroupParams, useGroups } from '../hooks/useGroups';
import ConfigurationTab from './ConfigurationTab';
import UsersTab from './UsersTab';

const TabPanel = styled(TabPanelBase)`
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: 0;
`;

type Props = {
  onClose: () => void;
};

const CONFIGURATION_TAB = 'configuration';
const USERS_TAB = 'users';

const GroupDrawer = ({ onClose }: Props) => {
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get(GROUP_ID);
  const [currentTab, setCurrentTab] = useState<string>(CONFIGURATION_TAB);

  const isOpen = !!groupId;
  const isCreateMode = groupId === NEW_VALUE;
  const isEditMode = !isCreateMode && !!groupId && groupId !== NEW_VALUE;

  const { groups, createGroupAsync, updateGroupAsync, isCreating, isUpdating } =
    useGroups();

  useEffect(() => {
    if (!isOpen) {
      setCurrentTab(CONFIGURATION_TAB);
    }
  }, [isOpen, setCurrentTab]);

  const group = useMemo(
    () =>
      isEditMode && groupId ? groups?.find((g) => g.id === groupId) : null,
    [groups, groupId, isEditMode],
  );

  const hasExternalManager = !!group?.external_manager;

  const defaultValues = useMemo(() => {
    if (group) {
      return {
        name: group.name,
        description: group.description,
        roles: group.roles.map((r) => r.id),
        userIds: group.users.map((u) => u.id),
      };
    }
    return {
      name: '',
      description: null,
      roles: [],
      userIds: [],
    };
  }, [group]);

  const methods = useForm<GroupFormData>({
    defaultValues,
  });

  useEffect(() => {
    if (isOpen) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods, isOpen]);

  const name = methods.watch('name');
  const description = methods.watch('description');
  const roles = methods.watch('roles');
  const userIds = methods.watch('userIds');

  const handleTabChange = (_event: SyntheticEvent, value: string) => {
    setCurrentTab(value);
  };

  const isConfigurationDirty = useMemo(() => {
    if (isCreateMode) {
      return !!(name || description);
    }
    if (!group) return false;
    return name !== group.name || description !== group.description;
  }, [isCreateMode, name, description, group]);

  const isRolesDirty = useMemo(() => {
    if (!group) return roles.length > 0;
    return !compareArraysValues(
      roles,
      group.roles.map((r) => r.id),
    );
  }, [roles, group]);

  const isUsersDirty = useMemo(() => {
    if (!group || isCreateMode) return userIds.length > 0;
    return !compareArraysValues(
      userIds,
      group.users.map((u) => u.id),
    );
  }, [userIds, group, isCreateMode]);

  const hasUnsavedChanges =
    isConfigurationDirty || isRolesDirty || isUsersDirty;

  const isFormValid = useMemo(() => !!name.trim(), [name]);

  const isSubmitDisabled = useMemo(() => {
    if (!isFormValid) return true;
    if (isCreateMode) return false; // Always allow save in create mode if valid
    return !isConfigurationDirty && !isUsersDirty && !isRolesDirty;
  }, [
    isFormValid,
    isCreateMode,
    isConfigurationDirty,
    isUsersDirty,
    isRolesDirty,
  ]);

  const isLoading = isCreating || isUpdating;

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingClose, setPendingClose] = useState<(() => void) | null>(null);

  const handleSave = async () => {
    if (isCreateMode) {
      await createGroupAsync({
        name,
        description: description || undefined,
        roles,
        userIds,
      });
    } else {
      const params: UpdateGroupParams = { groupId: group!.id };
      if (isConfigurationDirty) {
        params.configuration = { name, description };
      }
      if (isRolesDirty) {
        params.roles = roles;
      }
      if (isUsersDirty) {
        params.userIds = userIds;
      }
      await updateGroupAsync(params);
    }
    onClose();
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setPendingClose(() => onClose);
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setShowDiscardDialog(false);
    if (pendingClose) {
      pendingClose();
      setPendingClose(null);
    }
  };

  const handleCancelDiscard = () => {
    setShowDiscardDialog(false);
    setPendingClose(null);
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        width="small"
        title={`${isEditMode ? 'Edit' : 'Create'} group`}
        onClose={handleClose}
        submitButtonLabel="Save"
        onSubmit={handleSave}
        submitDisabled={isSubmitDisabled}
        isLoading={isLoading}
        noPadding
      >
        {isOpen && (
          <FormProvider {...methods}>
            <TabContext value={currentTab}>
              <FlexColumn sx={{ padding: '8px 24px' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={currentTab} onChange={handleTabChange}>
                    <Tab
                      label="Configuration"
                      value={CONFIGURATION_TAB}
                      wrapped
                    />
                    <Tab label="Users" value={USERS_TAB} wrapped />
                  </Tabs>
                </Box>

                <TabPanel value={CONFIGURATION_TAB} tabIndex={0}>
                  <ConfigurationTab hasExternalManager={hasExternalManager} />
                </TabPanel>

                <TabPanel value={USERS_TAB} tabIndex={1}>
                  <UsersTab hasExternalManager={hasExternalManager} />
                </TabPanel>
              </FlexColumn>
            </TabContext>
          </FormProvider>
        )}
      </Drawer>

      <Dialog
        open={showDiscardDialog}
        onClose={handleCancelDiscard}
        onConfirmButtonClick={handleDiscardChanges}
        titleIcon={<TrashIcon />}
        title="You have unsaved changes"
        message="Any unsaved changes will be discarded"
        confirmText="Discard"
        cancelText="Cancel"
      />
    </>
  );
};

export default GroupDrawer;
